import React, { useState, useEffect } from 'react';
import { useAuth, createApiClient } from '../App';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Gear,
  GoogleLogo,
  EnvelopeSimple,
  CalendarBlank,
  SignOut,
  Check,
  X,
  SpinnerGap,
  Export,
  Trash,
  ArrowSquareOut,
  User,
  LinkBreak,
  Link as LinkIcon
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const Settings = () => {
  const { user, token, logout } = useAuth();
  const api = createApiClient(token);

  const [calendarConnected, setCalendarConnected] = useState(false);
  const [gmailConnected, setGmailConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [icalUrl, setIcalUrl] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadIntegrationStatus();
  }, []);

  const loadIntegrationStatus = async () => {
    setLoading(true);
    try {
      const [gmailRes] = await Promise.all([
        api.get('/gmail/status').catch(() => ({ data: { connected: false } })),
      ]);
      setGmailConnected(gmailRes.data.connected);

      // Build iCal URL
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      setIcalUrl(`${backendUrl}/api/calendar/feed.ics?token=${token}`);
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectCalendar = async () => {
    try {
      const res = await api.get('/oauth/calendar/login');
      window.location.href = res.data.authorization_url;
    } catch (error) {
      toast.error('Failed to start Google Calendar connection');
    }
  };

  const handleConnectGmail = async () => {
    try {
      const res = await api.get('/oauth/gmail/login');
      window.location.href = res.data.authorization_url;
    } catch (error) {
      toast.error('Failed to start Gmail connection');
    }
  };

  const handleDisconnectGmail = async () => {
    try {
      await api.delete('/gmail/disconnect');
      setGmailConnected(false);
      toast.success('Gmail disconnected');
    } catch (error) {
      toast.error('Failed to disconnect Gmail');
    }
  };

  const handleCopyIcal = () => {
    navigator.clipboard.writeText(icalUrl);
    setCopied(true);
    toast.success('iCal URL copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleLogout = () => {
    logout();
    toast.success('Signed out');
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <SpinnerGap className="w-8 h-8 animate-spin text-[#A94438]" />
      </div>
    );
  }

  return (
    <div className="container-app py-6 sm:py-10 pb-28 sm:pb-10" data-testid="settings-page">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto space-y-6"
      >
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A1918]">Settings</h1>
          <p className="text-sm text-[#7A7873] mt-1">Manage your account and integrations</p>
        </div>

        {/* Profile */}
        <div className="card-elevated p-5 sm:p-6" data-testid="profile-section">
          <h2 className="font-heading text-xl font-semibold text-[#1A1918] mb-4 flex items-center gap-2">
            <User weight="duotone" className="w-5 h-5 text-[#A94438]" />
            Profile
          </h2>
          <div className="flex items-center gap-4">
            {user?.picture ? (
              <img
                src={user.picture}
                alt={user.name}
                className="w-14 h-14 rounded-full border-2 border-[#E8DCD0]"
                data-testid="profile-avatar"
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-[#E8DCD0] flex items-center justify-center">
                <User weight="bold" className="w-7 h-7 text-[#7A7873]" />
              </div>
            )}
            <div className="min-w-0">
              <p className="font-semibold text-[#1A1918] truncate" data-testid="profile-name">{user?.name || 'User'}</p>
              <p className="text-sm text-[#7A7873] truncate" data-testid="profile-email">{user?.email || ''}</p>
              <Badge variant="secondary" className="mt-1 text-xs">My Trackers SSO</Badge>
            </div>
          </div>
        </div>

        {/* Integrations */}
        <div className="card-elevated p-5 sm:p-6" data-testid="integrations-section">
          <h2 className="font-heading text-xl font-semibold text-[#1A1918] mb-5 flex items-center gap-2">
            <LinkIcon weight="duotone" className="w-5 h-5 text-[#4B5945]" />
            Integrations
          </h2>

          <div className="space-y-4">
            {/* Google Calendar */}
            <div className="flex items-center justify-between py-3 border-b border-[#EAE8E3] last:border-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <CalendarBlank weight="duotone" className="w-5 h-5 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-[#1A1918] text-sm">Google Calendar</p>
                  <p className="text-xs text-[#9A9590]">Sync planned visits to your calendar</p>
                </div>
              </div>
              <Button
                onClick={handleConnectCalendar}
                variant="outline"
                size="sm"
                className="flex-shrink-0 text-xs"
                data-testid="connect-calendar-btn"
              >
                <GoogleLogo weight="bold" className="w-4 h-4 mr-1" />
                Connect
              </Button>
            </div>

            {/* Gmail */}
            <div className="flex items-center justify-between py-3 border-b border-[#EAE8E3] last:border-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                  <EnvelopeSimple weight="duotone" className="w-5 h-5 text-red-500" />
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-[#1A1918] text-sm">Gmail</p>
                  <p className="text-xs text-[#9A9590]">Import booking confirmations from email</p>
                </div>
              </div>
              {gmailConnected ? (
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Badge className="bg-[#4B5945] text-white text-xs">
                    <Check weight="bold" className="w-3 h-3 mr-1" /> Connected
                  </Badge>
                  <button
                    onClick={handleDisconnectGmail}
                    className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                    title="Disconnect Gmail"
                    data-testid="disconnect-gmail-btn"
                  >
                    <LinkBreak weight="bold" className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              ) : (
                <Button
                  onClick={handleConnectGmail}
                  variant="outline"
                  size="sm"
                  className="flex-shrink-0 text-xs"
                  data-testid="connect-gmail-btn"
                >
                  <GoogleLogo weight="bold" className="w-4 h-4 mr-1" />
                  Connect
                </Button>
              )}
            </div>

            {/* iCal Feed */}
            <div className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-[#F5F1EC] flex items-center justify-center flex-shrink-0">
                  <Export weight="duotone" className="w-5 h-5 text-[#A94438]" />
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-[#1A1918] text-sm">iCal Feed</p>
                  <p className="text-xs text-[#9A9590]">Subscribe in any calendar app</p>
                </div>
              </div>
              <Button
                onClick={handleCopyIcal}
                variant="outline"
                size="sm"
                className="flex-shrink-0 text-xs"
                data-testid="copy-ical-btn"
              >
                {copied ? (
                  <><Check weight="bold" className="w-4 h-4 mr-1 text-green-600" /> Copied</>
                ) : (
                  <><Export weight="bold" className="w-4 h-4 mr-1" /> Copy URL</>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Account */}
        <div className="card-elevated p-5 sm:p-6" data-testid="account-section">
          <h2 className="font-heading text-xl font-semibold text-[#1A1918] mb-4 flex items-center gap-2">
            <Gear weight="duotone" className="w-5 h-5 text-[#7A7873]" />
            Account
          </h2>

          <div className="space-y-3">
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium text-sm text-[#1A1918]">Dining Wrapped</p>
                <p className="text-xs text-[#9A9590]">View your annual dining recap</p>
              </div>
              <a
                href="/wrapped"
                className="text-sm text-[#A94438] hover:underline font-medium flex items-center gap-1"
                data-testid="wrapped-link"
              >
                View <ArrowSquareOut weight="bold" className="w-4 h-4" />
              </a>
            </div>

            <div className="border-t border-[#EAE8E3] pt-3">
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="w-full justify-center text-red-500 hover:text-red-600 hover:bg-red-50"
                data-testid="logout-btn"
              >
                <SignOut weight="bold" className="w-5 h-5 mr-2" />
                Sign out
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Settings;
